import React, { useEffect, useState, Suspense, lazy } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Mission, MissionPanels } from './components/Mission';
import { ConsularBanner } from './components/ConsularBanner';
import { LandingPage } from './components/LandingPage';
import { FoundationalGiftPopup } from './components/FoundationalGiftPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { useLanguage } from './contexts/LanguageContext';
// Lazy-loaded below-the-fold sections
const CommunityStrength = lazy(() => import('./components/CommunityStrength').then(m => ({
  default: m.CommunityStrength
})));
const Leadership = lazy(() => import('./components/Leadership').then(m => ({
  default: m.Leadership
})));
const AdvisoryCouncil = lazy(() => import('./components/AdvisoryCouncil').then(m => ({
  default: m.AdvisoryCouncil
})));
const MakeDifferenceCTA = lazy(() => import('./components/MakeDifferenceCTA').then(m => ({
  default: m.MakeDifferenceCTA
})));
const NewsBlog = lazy(() => import('./components/NewsBlog').then(m => ({
  default: m.NewsBlog
})));
const MediaScroller = lazy(() => import('./components/MediaScroller').then(m => ({
  default: m.MediaScroller
})));
const ConsularAffairs = lazy(() => import('./components/ConsularAffairs').then(m => ({
  default: m.ConsularAffairs
})));
const BusinessInvestments = lazy(() => import('./components/BusinessInvestments').then(m => ({
  default: m.BusinessInvestments
})));
const OurPeopleStories = lazy(() => import('./components/OurPeopleStories').then(m => ({
  default: m.OurPeopleStories
})));
const NonprofitDirectory = lazy(() => import('./components/NonprofitDirectory').then(m => ({
  default: m.NonprofitDirectory
})));
const ExploreHonduras = lazy(() => import('./components/ExploreHonduras').then(m => ({
  default: m.ExploreHonduras
})));
const HondurasQuiz = lazy(() => import('./components/HondurasQuiz').then(m => ({
  default: m.HondurasQuiz
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const SocialMediaVoices = lazy(() => import('./components/SocialMediaVoices').then(m => ({
  default: m.SocialMediaVoices
})));
const ChatbaseWidget = lazy(() => import('./components/ChatbaseWidget').then(m => ({
  default: m.ChatbaseWidget
})));
// Lazy-loaded sub-pages
const AdminDashboard = lazy(() => import('./components/AdminDashboard').then(m => ({
  default: m.AdminDashboard
})));
const ConsularPage = lazy(() => import('./pages/ConsularPage').then(m => ({
  default: m.ConsularPage
})));
const BusinessMapPage = lazy(() => import('./pages/BusinessMapPage').then(m => ({
  default: m.BusinessMapPage
})));
const MemberProfilePage = lazy(() => import('./pages/MemberProfilePage').then(m => ({
  default: m.MemberProfilePage
})));
const NewsStoryPage = lazy(() => import('./pages/NewsStoryPage').then(m => ({
  default: m.NewsStoryPage
})));
const MembersDirectoryPage = lazy(() => import('./pages/MembersDirectoryPage').then(m => ({
  default: m.MembersDirectoryPage
})));
const SaveTheDatePage = lazy(() => import('./pages/SaveTheDatePage').then(m => ({
  default: m.SaveTheDatePage
})));
// Lightweight loading placeholder — subtle pulse instead of spinner to reduce visual jank
function SectionLoader() {
  return <div className="w-full py-16 flex items-center justify-center" data-id="element-0" data-mp="1m4weckq03jht">
      <div className="flex items-center gap-2" data-id="element-1" data-mp="1m78z15n587yk">
        <div className="w-2 h-2 rounded-full bg-haa-blue/30 animate-pulse" data-id="element-2" data-mp="6o5uvka7smiq" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/20 animate-pulse" style={{
        animationDelay: '150ms'
      }} data-id="element-3" data-mp="apr9ba2ed8v1" />
        <div className="w-2 h-2 rounded-full bg-haa-blue/10 animate-pulse" style={{
        animationDelay: '300ms'
      }} data-id="element-4" data-mp="qz8fp91mptrv" />
      </div>
    </div>;
}
function AppContent({
  showLanding,
  setShowLanding
}: {
  showLanding: boolean;
  setShowLanding: (v: boolean) => void;
}) {
  const {
    setLanguage
  } = useLanguage();
  const [pendingScroll, setPendingScroll] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash;
    return isSectionAnchor(hash) ? resolveAnchor(hash.slice(1)) : null;
  });
  const handleSelectLanguage = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
  };
  const handleConsularAccess = (lang: 'EN' | 'ES') => {
    setLanguage(lang);
    setShowLanding(false);
    setPendingScroll('consular-affairs');
  };
  // React to in-page hash changes (e.g. opening a /#leadership link while already on the site)
  useEffect(() => {
    const handler = () => {
      const hash = window.location.hash;
      if (isSectionAnchor(hash)) {
        setShowLanding(false);
        setPendingScroll(resolveAnchor(hash.slice(1)));
      }
    };
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, [setShowLanding]);
  useEffect(() => {
    if (!showLanding && pendingScroll) {
      // Lazy-loaded sections may not be mounted yet — retry briefly until the element exists.
      const start = Date.now();
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(pendingScroll);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          setPendingScroll(null);
          return;
        }
        if (Date.now() - start < 4000) {
          setTimeout(tryScroll, 150);
        } else {
          setPendingScroll(null);
        }
      };
      const timer = setTimeout(tryScroll, 100);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
  }, [showLanding, pendingScroll]);
  if (showLanding) {
    return <LandingPage onSelectLanguage={handleSelectLanguage} onConsularAccess={handleConsularAccess} data-id="element-5" data-mp="2asikm8quqfsr" />;
  }
  return <div className="font-nunito w-full min-h-screen overflow-x-hidden" data-id="element-6" data-mp="25va2g85pebri">
      <Navbar data-id="element-7" data-mp="2kp54ovbct2tq" />
      <FoundationalGiftPopup data-id="element-8" data-mp="2kolnjgtj2igr" />
      <main data-id="element-9" data-mp="2frd47cwn88dx">
        <Hero data-id="element-10" data-mp="2frljtck5hrst" />
        <ConsularBanner data-id="element-11" data-mp="2fs4xui6ojw1k" />
        <Mission data-id="element-12" data-mp="2fqtvmmsvuwck" />

        {/* Each section loads independently for faster perceived performance */}
        <Suspense fallback={<SectionLoader data-id="element-14" data-mp="2tkukmou194zq" />} data-id="element-13" data-mp="2ti8gz0a9j9ms">
          <OurPeopleStories data-id="element-15" data-mp="2phet26wtlutw" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-17" data-mp="2pj9cthwcvgwe" />} data-id="element-16" data-mp="2phohvr64pvy0">
          <MakeDifferenceCTA data-id="element-18" data-mp="33go6iytn6au6" />
        </Suspense>
        <div className="max-w-5xl mx-auto px-4" data-id="element-19" data-mp="33hfqtdhsgm55">
          <div className="h-px bg-gradient-to-r from-transparent via-haa-blue/20 to-transparent" data-id="element-20" data-mp="2yi37v3u1fz7u" />
        </div>
        <Suspense fallback={<SectionLoader data-id="element-22" data-mp="3i4o5wpe7xbme" />} data-id="element-21" data-mp="3i33ayynzrqo0">
          <NewsBlog data-id="element-23" data-mp="3i4eklf44bsn1" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-25" data-mp="3n2zg518fdo55" />} data-id="element-24" data-mp="3n2g39yxmtpxz">
          <MediaScroller data-id="element-26" data-mp="3n27ou2lv2cko" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-28" data-mp="3868esmf6y2cw" />} data-id="element-27" data-mp="385p861uzr0e6">
          <CommunityStrength data-id="element-29" data-mp="384xc6pxhf0nd" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-31" data-mp="3d3qrmnwbx1q6" />} data-id="element-30" data-mp="3d49ztcw6gbr0">
          <MissionPanels data-id="element-32" data-mp="3d2ywfe6n960f" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-34" data-mp="2zcvev0ipm0hb" />} data-id="element-33" data-mp="2zdeziozqv2z1">
          <Leadership data-id="element-35" data-mp="2zbkd168tr2su" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-37" data-mp="344di5v87gd5g" />} data-id="element-36" data-mp="343vcy93keole">
          <AdvisoryCouncil data-id="element-38" data-mp="344n7dglfega3" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-40" data-mp="2pfhba1hvx66f" />} data-id="element-39" data-mp="2pez7mjsw7poh">
          <MakeDifferenceCTA data-id="element-41" data-mp="2pga8obuyejbw" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-43" data-mp="2udivipr1rbjh" />} data-id="element-42" data-mp="2ue225ab8ydi7">
          <BusinessInvestments data-id="element-44" data-mp="2uetywogkyha2" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-46" data-mp="2gdraw660ywwi" />} data-id="element-45" data-mp="2ge9bfezpzxa8">
          <ConsularAffairs data-id="element-47" data-mp="2gfkgdi4wjb2x" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-49" data-mp="2kge3i9aislur" />} data-id="element-48" data-mp="2kfuwvoqbljw1">
          <SocialMediaVoices data-id="element-50" data-mp="2keky0xbn87oc" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-52" data-mp="30vx17o19hjhk" />} data-id="element-51" data-mp="30vdi43zvkp1y">
          <NonprofitDirectory data-id="element-53" data-mp="30u2imgdgq3gn" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-55" data-mp="2vvr966r2xuyy" />} data-id="element-54" data-mp="2vwanldhiu188">
          <HondurasQuiz data-id="element-56" data-mp="2vxlsjgmpdf0x" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-58" data-mp="2qwocb9l96v22" />} data-id="element-57" data-mp="2qx7ixu5gdx0s">
          <ExploreHonduras data-id="element-59" data-mp="2qvwgbxnquvb9" />
        </Suspense>
        <Suspense fallback={<SectionLoader data-id="element-61" data-mp="2lyczcpznw4fm" />} data-id="element-60" data-mp="2qz0p78c5mf89">
          <FAQ data-id="element-62" data-mp="2lx34e9o3vcd7" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-63" data-mp="2m0h1p3hwwvds">
        <Footer data-id="element-64" data-mp="2hwrheq89szyk" />
      </Suspense>
      <Suspense fallback={null} data-id="element-65" data-mp="2hy2kentw63om">
        <ChatbaseWidget data-id="element-66" data-mp="2d5sz5l3q1rso" />
      </Suspense>
    </div>;
}
// Short, shareable aliases that resolve to the actual section IDs in the DOM.
// Example: /#council scrolls to the <section id="advisory-council">.
const SECTION_ALIASES: Record<string, string> = {
  council: 'advisory-council',
  members: 'our-stories',
  consular: 'consular-affairs',
  business: 'business-investments',
  nonprofit: 'nonprofit-directory',
  explore: 'explore-honduras',
  trivia: 'honduras-trivia',
  socials: 'social-voices'
};
// Section anchors that, when present in the URL hash, should bypass the landing page
// and scroll directly to that section on the main site. Both canonical IDs and the
// friendly aliases above are accepted.
const SECTION_ANCHORS = new Set(['home', 'mission', 'community', 'leadership', 'advisory-council', 'our-stories', 'news', 'social-voices', 'explore-honduras', 'honduras-trivia', 'nonprofit-directory', 'business-investments', 'consular-affairs', 'faq', 'contact', ...Object.keys(SECTION_ALIASES)]);
function resolveAnchor(id: string): string {
  return SECTION_ALIASES[id] ?? id;
}
function isSectionAnchor(hash: string): boolean {
  if (!hash || !hash.startsWith('#')) return false;
  if (hash.startsWith('#/')) return false;
  const id = hash.slice(1);
  return SECTION_ANCHORS.has(id);
}
export function App() {
  const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
  const [showLanding, setShowLanding] = useState(!isSectionAnchor(initialHash));
  const [showAdmin, setShowAdmin] = useState(() => window.location.hash === '#/admin');
  const [showConsular, setShowConsular] = useState(() => window.location.hash === '#/consular');
  const [showBusinessMap, setShowBusinessMap] = useState(() => window.location.hash.startsWith('#/business-map'));
  const [businessMapLang, setBusinessMapLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/business-map')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  const [showMembers, setShowMembers] = useState(() => window.location.hash === '#/members');
  const [showSaveTheDate, setShowSaveTheDate] = useState(() => window.location.hash === '#/save-the-date');
  const [showMember, setShowMember] = useState(() => window.location.hash.startsWith('#/member/'));
  const [memberSlug, setMemberSlug] = useState(() => {
    const hash = window.location.hash;
    return hash.startsWith('#/member/') ? hash.replace('#/member/', '') : '';
  });
  const [showNews, setShowNews] = useState(() => window.location.hash.startsWith('#/news/'));
  const [newsPostId, setNewsPostId] = useState(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return '';
    const raw = hash.replace('#/news/', '');
    return raw.split('?')[0];
  });
  const [newsLang, setNewsLang] = useState<'EN' | 'ES'>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#/news/')) return 'EN';
    const match = hash.match(/[?&]lang=(EN|ES)/);
    return match ? match[1] as 'EN' | 'ES' : 'EN';
  });
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      setShowAdmin(hash === '#/admin');
      setShowConsular(hash === '#/consular');
      const isBusinessMap = hash.startsWith('#/business-map');
      setShowBusinessMap(isBusinessMap);
      if (isBusinessMap) {
        const bmLangMatch = hash.match(/[?&]lang=(EN|ES)/);
        setBusinessMapLang(bmLangMatch ? bmLangMatch[1] as 'EN' | 'ES' : 'EN');
      }
      setShowMembers(hash === '#/members');
      setShowSaveTheDate(hash === '#/save-the-date');
      const isMember = hash.startsWith('#/member/');
      setShowMember(isMember);
      if (isMember) {
        setMemberSlug(hash.replace('#/member/', ''));
      }
      const isNews = hash.startsWith('#/news/');
      setShowNews(isNews);
      if (isNews) {
        const raw = hash.replace('#/news/', '');
        setNewsPostId(raw.split('?')[0]);
        const langMatch = raw.match(/[?&]lang=(EN|ES)/);
        setNewsLang(langMatch ? langMatch[1] as 'EN' | 'ES' : 'EN');
      }
      // When navigating back to root from a sub-page, skip the landing page
      if (hash === '' || hash === '#') {
        setShowLanding(false);
      }
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);
  const handleReturnToLanding = () => {
    setShowLanding(true);
  };
  if (showAdmin) {
    return <Suspense fallback={<SectionLoader data-id="element-68" data-mp="15oeuwiju79wg" />} data-id="element-67" data-mp="15pzpu9a2cuuu">
        <AdminDashboard onBack={() => {
        window.location.hash = '';
        setShowAdmin(false);
      }} data-id="element-69" data-mp="15p6kncqr6det" />
      </Suspense>;
  }
  if (showConsular) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-70" data-mp="1fhcllbyzg565">
        <Suspense fallback={<SectionLoader data-id="element-72" data-mp="rq72mnxqwwrm" />} data-id="element-71" data-mp="rqqfhq8jguys">
          <ConsularPage data-id="element-73" data-mp="rri6sov3s6j9" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showBusinessMap) {
    return <LanguageProvider initialLanguage={businessMapLang} onLanguageSwitch={handleReturnToLanding} data-id="element-74" data-mp="11nlcg4p5ynay">
        <Suspense fallback={<SectionLoader data-id="element-76" data-mp="wn70ibkfyt7n" />} data-id="element-75" data-mp="wnqjlvltvnn9">
          <BusinessMapPage data-id="element-77" data-mp="wr4md4yelyv8" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMembers) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-78" data-mp="283xbe7zt897l">
        <Suspense fallback={<SectionLoader data-id="element-80" data-mp="2869p28yjbxeu" />} data-id="element-79" data-mp="283nlekerm21w">
          <MembersDirectoryPage data-id="element-81" data-mp="hncwatbs1qp5" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showSaveTheDate) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-82" data-mp="mmpj5e2nexrq">
        <Suspense fallback={<SectionLoader data-id="element-84" data-mp="qn8az84jk3tx" />} data-id="element-83" data-mp="qnrnuafc4213">
          <SaveTheDatePage data-id="element-85" data-mp="qlyf8pl5v7qg" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showMember) {
    return <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-86" data-mp="vkhygi8de5f4">
        <Suspense fallback={<SectionLoader data-id="element-88" data-mp="10lffuslk2vfk" />} data-id="element-87" data-mp="10kw9881cvtgu">
          <MemberProfilePage slug={memberSlug} data-id="element-89" data-mp="15kse09hvnoxg" />
        </Suspense>
      </LanguageProvider>;
  }
  if (showNews) {
    return <LanguageProvider initialLanguage={newsLang} data-id="element-90" data-mp="1ab8szy1cjilx">
        <Suspense fallback={<SectionLoader data-id="element-92" data-mp="1ed2mnmd4jmcl" />} data-id="element-91" data-mp="1adlg0plyoj5u">
          <NewsStoryPage postId={newsPostId} data-id="element-93" data-mp="1edc7ywn855by" />
        </Suspense>
      </LanguageProvider>;
  }
  return <>
      <GoogleAnalytics data-id="element-94" data-mp="1jdhyualo6nsz" />
      <LanguageProvider onLanguageSwitch={handleReturnToLanding} data-id="element-95" data-mp="1ja2kj9ztlism">
        <AppContent showLanding={showLanding} setShowLanding={setShowLanding} data-id="element-96" data-mp="1oc1oom3dwjqm" />
      </LanguageProvider>
    </>;
}